import React, { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { ASSET_CHARGES_INFO_QUERY, ASSET_REPLAY_LOG_QUERY, PROJECT_BILLING_RATES_QUERY } from "../../../constants/graphql/queries";
import Step1InformationView from "./Step1InformationView";
import Step2EditView from "./Step2EditView";
import { getPalletRateKey, getUnitRateKey } from "./billingTypeConfig";

// Calculate frontend charges for historical data display
const calculateDisplayCharges = (asset, projectBillingRates) => {
  if (!asset.billingType) return null;

  const billingType = String(asset.billingType || "").toLowerCase();
  const storageRates = projectBillingRates?.storage || {};

  if (billingType.includes('unit/item')) {
    const unitType = asset.unitType || 'standard_unit';
    const rateKey = getUnitRateKey(unitType);
    const rate = parseFloat(storageRates[rateKey]) || 0;
    const quantity = asset.warehouseQuantity || 0;
    const total = quantity * rate;

    return {
      total,
      billing_type: asset.billingType,
      [unitType]: {
        quantity,
        rate_used: rate,
        total,
      }
    };
  }

  if (!billingType.includes('pallet')) return null;

  const palletType = asset.palletType || 'Standard Pallet';
  const rateKey = getPalletRateKey(palletType);
  const rate = parseFloat(storageRates[rateKey]) || 0;
  const numLocations = asset.rackLocations?.length || 0;
  const total = numLocations * rate;

  return {
    total,
    billing_type: asset.billingType,
    normal_pallet: {
      quantity: numLocations,
      rate_used: rate,
      total,
    }
  };
};

export default function AssetChargesSummaryModal({ 
  assetId, 
  assetReplayLogId, 
  startDate, 
  endDate, 
  editingCharges, 
  detailsExpanded,
  onEditCharges, 
  onStopEditing,
  onToggleDetails,
  onUpdateReplayLogId,
  onClose 
}) {
  const isHistoricalData = Boolean(assetReplayLogId);
  const step = editingCharges ? 2 : 1;

  // Fetch the current asset data
  const { data: assetData, loading: assetLoading, error: assetError } = useQuery(ASSET_CHARGES_INFO_QUERY, {
    variables: { assetId },
    skip: !assetId,
  });

  // Fetch the replay log data if we have a replay log ID
  const { data: replayLogData, loading: replayLogLoading } = useQuery(ASSET_REPLAY_LOG_QUERY, {
    variables: { id: assetReplayLogId },
    skip: !assetReplayLogId,
  });

  // Fetch project billing rates for frontend calculation
  const { data: projectData } = useQuery(PROJECT_BILLING_RATES_QUERY, {
    variables: { projectId: assetData?.asset?.project?.id },
    skip: !assetData?.asset?.project?.id || !isHistoricalData,
  });

  // Merge asset data with replay log data when available
  const asset = useMemo(() => {
    if (!assetData?.asset) return null;

    const baseAsset = assetData.asset;
    
    // If we have replay log data, override the relevant fields
    if (replayLogData?.assetReplayLog) {
      const replayLog = replayLogData.assetReplayLog;
      const mergedAsset = {
        ...baseAsset,
        warehouseQuantity: replayLog.warehouseQty,
        rackLocations: replayLog.rackLocations,
      };

      // For historical data, calculate frontend charges for display
      if (isHistoricalData && projectData?.project?.billingRates) {
        const displayCharges = calculateDisplayCharges(mergedAsset, projectData.project.billingRates);
        if (displayCharges) {
          mergedAsset.storageCharges = displayCharges;
          mergedAsset.billingCharges = {
            ...(mergedAsset.billingCharges || {}),
            storage_total: displayCharges.total,
            grand_total: displayCharges.total,
            calculated_at: new Date().toISOString(),
          };
        }
      }

      return mergedAsset;
    }

    return baseAsset;
  }, [assetData, replayLogData, isHistoricalData, projectData]);

  const loading = assetLoading || (isHistoricalData && replayLogLoading);
  const error = assetError;
  const billingType = asset?.billingType || asset?.storageCharges?.billing_type;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      sx={{ '& .MuiDialog-paper': { maxWidth: '600px', width: '100%' } }}
      aria-labelledby="asset-charges-summary-title"
    >
      <DialogTitle id="asset-charges-summary-title">
        <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
          Storage Charges Summary
        </Typography>
        <IconButton
          onClick={onClose}
          size="large"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ 
        p: 5, 
        display: "flex", 
        flexDirection: "column", 
        gap: 3,
        ...(step === 2 && { minHeight: "60vh", maxHeight: "80vh" })
      }}>
        {loading && <LinearProgress />}
        
        {error && (
          <Box textAlign="center" color="error.main" py={2}>
            Failed to load asset data
          </Box>
        )}

        {!loading && !error && !asset && (
          <Box textAlign="center" color="text.secondary" py={2}>
            Asset not found
          </Box>
        )}

        {asset && !billingType && step === 1 && (
          <Box textAlign="center" color="text.secondary" py={4}>
            <Typography variant="body1" fontWeight={500}>
              No billing type set for this item
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1} mb={2}>
              Configure a billing type to see charges
            </Typography>
            <Button variant="contained" color="primary" onClick={onEditCharges}>
              Configure Billing
            </Button>
          </Box>
        )}

        {asset && billingType && step === 1 && (
          <Step1InformationView 
            asset={asset}
            isHistoricalData={isHistoricalData}
            startDate={startDate}
            endDate={endDate}
            detailsExpanded={detailsExpanded}
            onMakeChanges={onEditCharges}
            onToggleDetails={onToggleDetails}
          />
        )}

        {asset && step === 2 && (
          <Step2EditView 
            asset={asset}
            isHistoricalData={isHistoricalData}
            assetReplayLogId={assetReplayLogId}
            onUpdateReplayLogId={onUpdateReplayLogId}
            onBack={onStopEditing}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
